import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { History, TrendingUp, TrendingDown, MinusCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ShareCapitalHistoryEntry {
  id: string;
  company_id: string;
  previous_authorized_shares: number | null;
  new_authorized_shares: number | null;
  changed_by: string;
  changed_at: string;
  notes: string | null;
  user_email?: string;
}

interface ShareCapitalHistoryProps {
  companyId: string;
}

export function ShareCapitalHistory({ companyId }: ShareCapitalHistoryProps) {
  const [history, setHistory] = useState<ShareCapitalHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [companyId]);

  const loadHistory = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('share_capital_history')
        .select('*')
        .eq('company_id', companyId)
        .order('changed_at', { ascending: false });

      if (error) throw error;

      // Fetch user emails for changed_by
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map((entry) => entry.changed_by))];

        // Try to get emails from auth.users via RPC or profiles table
        const { data: usersData } = await supabase.rpc('get_users_by_ids', {
          user_ids: userIds,
        });

        const userMap = new Map(usersData?.map((u: any) => [u.id, u.email]) || []);

        const enrichedData = data.map((entry) => ({
          ...entry,
          user_email: userMap.get(entry.changed_by) || 'Unknown User',
        }));

        setHistory(enrichedData);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error('Error loading share capital history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChangeIcon = (previous: number | null, current: number | null) => {
    if (previous === null && current !== null) {
      return <TrendingUp className="h-4 w-4 text-success" />;
    }
    if (previous !== null && current === null) {
      return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
    }
    if (previous !== null && current !== null) {
      return previous < current ? (
        <TrendingUp className="h-4 w-4 text-success" />
      ) : (
        <TrendingDown className="h-4 w-4 text-warning" />
      );
    }
    return null;
  };

  const getChangeText = (previous: number | null, current: number | null) => {
    if (previous === null && current !== null) {
      return `Set to ${current.toLocaleString()}`;
    }
    if (previous !== null && current === null) {
      return 'Removed cap (unlimited)';
    }
    if (previous !== null && current !== null) {
      const diff = current - previous;
      return `${previous.toLocaleString()} → ${current.toLocaleString()} (${diff > 0 ? '+' : ''}${diff.toLocaleString()})`;
    }
    return 'No change';
  };

  const getChangeBadge = (previous: number | null, current: number | null) => {
    if (previous === null && current !== null) {
      return <Badge variant="default">Initial Setup</Badge>;
    }
    if (previous !== null && current === null) {
      return <Badge variant="secondary">Cap Removed</Badge>;
    }
    if (previous !== null && current !== null && previous < current) {
      return <Badge variant="default">Increased</Badge>;
    }
    if (previous !== null && current !== null && previous > current) {
      return <Badge variant="outline">Decreased</Badge>;
    }
    return null;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Share Capital History
          </CardTitle>
          <CardDescription>Audit log of all authorized share changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Share Capital History
          </CardTitle>
          <CardDescription>Audit log of all authorized share changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <History className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No history available</p>
            <p className="text-sm text-muted-foreground">
              Changes to authorized shares will appear here
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Share Capital History
        </CardTitle>
        <CardDescription>
          Complete audit log of all authorized share capital changes
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Changed By</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Details</TableHead>
              <TableHead>Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="font-medium">
                  <div className="flex flex-col">
                    <span className="text-sm">
                      {new Date(entry.changed_at).toLocaleDateString()}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(entry.changed_at), { addSuffix: true })}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{entry.user_email}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(entry.previous_authorized_shares, entry.new_authorized_shares)}
                    <span className="text-sm font-medium">
                      {getChangeText(entry.previous_authorized_shares, entry.new_authorized_shares)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {entry.notes ? (
                    <span className="text-sm text-muted-foreground">{entry.notes}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {getChangeBadge(entry.previous_authorized_shares, entry.new_authorized_shares)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
